import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import StatCards from './StatCards';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminGradesView.css';
import { scopeForAdmin, schoolMatches } from '../utils/schoolScope';

function SuperAdminGradesView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminGrades: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      classesData = scopeForAdmin(classesData, adminData);
      console.log('🔥 AdminGrades: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminGrades: Error loading classes:', error);
    }
  }, [adminData]);

  const loadGrades = useCallback(async () => {
    console.log('🔥 AdminGrades: Loading grades');
    try {
      const gradesData = [];
      for (const cls of classes) {
        // Load students in this class for name lookup
        const studentsSnapshot = await getDocs(collection(db, 'classes', cls.id, 'students'));
        const studentsById = {};
        studentsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          studentsById[doc.id] = data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || doc.id;
          if (data.studentId) {
            studentsById[data.studentId] = studentsById[doc.id];
          }
        });

        const assignmentsSnapshot = await getDocs(collection(db, 'classes', cls.id, 'assignments'));
        for (const assignmentDoc of assignmentsSnapshot.docs) {
          const assignmentId = assignmentDoc.id;
          const assignmentName = assignmentDoc.data().title || assignmentId;
          const gradesSnapshot = await getDocs(
            collection(db, 'classes', cls.id, 'assignments', assignmentDoc.id, 'grades')
          );
          gradesSnapshot.docs.forEach(doc => {
            const gradeData = doc.data();
            const studentId = gradeData.studentId;
            gradesData.push({
              id: doc.id,
              ...gradeData,
              classId: cls.id,
              className: cls.name,
              schoolId: cls.schoolId,
              assignmentId,
              assignmentName,
              studentName: studentsById[studentId] || studentId
            });
          });
        }
      }
      console.log('🔥 AdminGrades: Grades loaded', gradesData.length, gradesData);
      setGrades(gradesData);
    } catch (error) {
      console.error('🔥 AdminGrades: Error loading grades:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadGrades();
    }
  }, [classes, loadGrades]);

  const getFilteredGrades = () => {
    if (selectedClass === 'all') return grades;
    return grades.filter(grade => grade.classId === selectedClass);
  };

  const calculateAverage = (gradeList) => {
    if (gradeList.length === 0) return 0;
    const sum = gradeList.reduce((acc, grade) => acc + (grade.score || 0), 0);
    return (sum / gradeList.length).toFixed(1);
  };

  const getGradeDistribution = (gradeList) => {
    const distribution = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    gradeList.forEach(grade => {
      const percentage = (grade.score / grade.maxScore) * 100;
      if (percentage >= 90) distribution.A++;
      else if (percentage >= 80) distribution.B++;
      else if (percentage >= 70) distribution.C++;
      else if (percentage >= 60) distribution.D++;
      else distribution.F++;
    });
    return distribution;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredGrades = getFilteredGrades();
  const average = calculateAverage(filteredGrades);
  const distribution = getGradeDistribution(filteredGrades);
  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || sid || '';

  return (
    <div className="admin-grades-view">
      <div className="view-header">
        <h2>Grade Analytics</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
      </div>

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <div className="stats-section">
        <h3>Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{filteredGrades.length}</h3>
              <p>Total Grades</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-info">
              <h3>{average}%</h3>
              <p>Average Score</p>
            </div>
          </div>
        </div>
      </div>

      <StatCards items={[]} charts={[
        { title: 'Class Average Grades (%)', type: 'bar', data: classes.map(c => { const g = grades.filter(x => x.classId === c.id); return { name: c.name, value: g.length ? Math.round(g.reduce((a, x) => a + ((x.score / (x.maxScore || 100)) * 100 || 0), 0) / g.length) : 0 }; }) },
        { title: 'Grade Distribution', type: 'bar', data: ['A', 'B', 'C', 'D', 'F'].map(k => ({ name: k, value: distribution[k] })) }
      ]} />

      <div className="stats-section">
        <h3>Grade Distribution</h3>
        <div className="grade-distribution">
          <div className="grade-bar">
            <span className="grade-label">A</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.A / filteredGrades.length) * 100 || 0}%`, background: '#22c55e' }}></div>
            </div>
            <span className="grade-count">{distribution.A}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">B</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.B / filteredGrades.length) * 100 || 0}%`, background: '#22c55e' }}></div>
            </div>
            <span className="grade-count">{distribution.B}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">C</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.C / filteredGrades.length) * 100 || 0}%`, background: '#f59e0b' }}></div>
            </div>
            <span className="grade-count">{distribution.C}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">D</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.D / filteredGrades.length) * 100 || 0}%`, background: '#f97316' }}></div>
            </div>
            <span className="grade-count">{distribution.D}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">F</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.F / filteredGrades.length) * 100 || 0}%`, background: '#ef4444' }}></div>
            </div>
            <span className="grade-count">{distribution.F}</span>
          </div>
        </div>
      </div>

      {filteredGrades.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Grades</h3>
          <p>No grades recorded yet</p>
        </div>
      ) : (
        <div className="grades-list">
          {filteredGrades.map(grade => (
            <div key={grade.id} className="grade-card">
              <div className="grade-info">
                <h4>Assignment: {grade.assignmentName || 'N/A'}</h4>
                <p>Student: {grade.studentName || grade.studentId || 'N/A'}</p>
                <p>Class: {grade.className || 'N/A'}</p>
                {schoolNameFor(grade.schoolId) && <p>School: {schoolNameFor(grade.schoolId)}</p>}
                <p>Score: {grade.score} / {grade.maxScore}</p>
                <p>Percentage: {((grade.score / grade.maxScore) * 100).toFixed(1)}%</p>
              </div>
              <div className="grade-display">
                <span className="grade-percentage">{((grade.score / grade.maxScore) * 100).toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuperAdminGradesView;
